/* eslint no-param-reassign: ["error", { "props": true, ignorePropertyModificationsFor: ["draft"] }] */

import {
  ApolloError,
  ApolloLink,
  FetchResult,
  NextLink,
  Operation,
} from '@apollo/client'
import { createOperation, transformOperation } from '@apollo/client/link/utils'
import { Observable, Concast } from '@apollo/client/utilities'
import type { GraphQLFormattedError } from 'graphql'
import { produce } from 'immer'

import { $$auth } from '@shared/auth'

import { REFRESH_TOKENS_MUTATION } from 'src/apollo/mutations/AUTH_MUTATIONS'
import { ClientContext, RefreshMutationResponse } from 'src/apollo/utils/types'
import { GraphQLErrorCode } from 'src/constants/errorCodeConstants'

/**
 * # TokenManagerLink
 *
 * TokenManagerLink is responsible for handling token expiration and refresh.
 *
 * It handles EXPIRED_SESSION error by running a single, multicast refresh operation
 * and retrying the original operation once refresh succeeds.
 *
 * ## Request Flow Schema
 *
 *                              header
 * ┌───────────────┐       ┌──────────────┐    forward
 * │ -> .request() ├───────► .authorize() ├───────────────┐
 * └────▲─────▲────┘       └──────────────┘         ┌─────▼─────┐
 *      │     │                                     │ Next Link │
 *      │     │  success   ┌──────────────────┐     └─────┬─────┘
 *      │     └────────────┤ isExpiredError() ◄───────────┘
 *      │                  └────────┬─────────┘
 *      │ retry after refresh       │
 *      └────────────────────┐      │ EXPIRED_SESSION
 *                           │      │
 *         reuse existing  ┌─┴──────▼┐
 *       ┌─────────────────┤ .lock() │
 *       │   if present    └─▲──────┬┘
 *       │                   │      │
 * ┌─────▼──────────┐      ┌─┴──────▼───┐      forward
 * │ this.refresher ◄~~~~~~│ .refresh() ├─────────────────┐
 * └────────────────┘      └─▲──────────┘           ┌─────▼─────┐
 *                           │                      │ Next Link │
 *                         ┌─┴───────┐              └─────┬─────┘
 *                         │ .save() ◄────────────────────┘
 *                         └─────────┘
 *
 * ### Normal Operation
 *
 * 1. An operation comes into `.request()` from Apollo Client
 * 2. We add an `Authorization` header using stored access token
 * 3. Operation is then `forward`ed to the next link in chain, making a network request
 * 4. When a response comes in, we check if there's an `EXPIRED_ERROR`
 *   - When no such error is present, we pass the response on upward the chain
 *   - However, when there *is* and EXPIRED_ERROR, we start a refresh
 *
 * ### Refresh Operation
 * 1. We lock the original operation from completion, so that uplink is *not* notified of our workings
 * 2. We check if there's an ongoing refresh
 *   - When a refresh is already in flight, we subscribe to it finishing and proceed to p.6
 *   - Otherwise, we start our own refresh
 * 3. We create a new `Operation` with a refresh mutation + a token, and `forward` it
 * 4. When a refresh response comes in,
 *   - On *failure*, we route the error from failure to the original operation and bail
 *   - On *success*, we save the tokens to the storage
 * 5. With tokens updated, we release the lock on all pending operations
 * 6. As soon as lock is released, this allows them to retry via `onRetry`,
 *    passing all operations back again to Token Manager Link
 *
 * ## Notes
 *
 * 1. This does not (yet) support cancellable operations beyond the first, main operation.
 *    As soon as the refresh process starts, you can not cancel the underlying operation.
 * 2. This does not offer any protections against infinite looping requests, where
 *    refresh operation does not *actually* result in solving EXPIRED_SESSION error.
 */

export class TokenManagerLink extends ApolloLink {
  private refresher: Observable<void> | null = null

  request(operation: Operation, forward: NextLink) {
    return new Observable<FetchResult>((observer) => {
      TokenManagerLink.authorize(operation)

      const request = forward(operation)
      let isLocked = false

      const retry = () => this.request(operation, forward).subscribe(observer)

      const fail = (error: ApolloError) =>
        Promise.resolve(error)
          .then(TokenManagerLink.unwrap)
          .then(observer.next.bind(observer))
          .then(observer.complete.bind(observer))
          .catch(observer.error.bind(observer))

      const onNext = (result: FetchResult) => {
        const isExpired = result.errors?.some(isExpiredError) ?? false
        if (!isExpired) return observer.next(result)

        isLocked = true

        const obs = { complete: retry, error: fail }
        return this.lock(forward).subscribe(obs)
      }

      return request.subscribe({
        next: onNext,
        error: observer.error.bind(observer),
        complete: () => {
          // once our own handler starts, lock completion to
          // prevent the old operation from bubbling up the link chain
          if (!isLocked) observer.complete()
        },
      })
    })
  }

  private lock(forward: NextLink): Observable<void> {
    if (!this.refresher) {
      /** resets the lock so that the next refresh will fire a new network request */
      const reset = () => (this.refresher = null)

      this.refresher = this.refresh(forward)
      this.refresher = tap(this.refresher, { error: reset, complete: reset })

      // `Concast` allows sharing a single underlying `refresher` among
      // several subscribers, preventing duplicate refresh requests
      this.refresher = new Concast([this.refresher])
    }

    return this.refresher
  }

  /** Creates a Cold Observable to refresh the token */
  private refresh(forward: NextLink): Observable<void> {
    const operation = this.createRefresh()
    const observable = forward(operation)

    // when `.map` handler fails, the error is automatically
    // routed into `error` flow, preventing an infinite cycle
    return observable.map(TokenManagerLink.save)
  }

  private createRefresh() {
    // eslint-disable-next-line effector/no-getState
    const token = $$auth.token.$refresh.getState()
    const headers = { 'X-Refresh-Token': `Bearer ${token}` }

    const operation = transformOperation({ query: REFRESH_TOKENS_MUTATION })
    return createOperation({ headers }, operation)
  }

  private static save(
    this: void,
    { data, errors }: FetchResult<RefreshMutationResponse>,
  ) {
    const { access_token: access, refresh_token: refresh } =
      data?.refreshTokens ?? {}

    if (!access || !refresh) throw new ApolloError({ graphQLErrors: errors })

    $$auth.token.save({ access, refresh })
  }

  private static unwrap(this: void, error: unknown): FetchResult {
    if (error instanceof ApolloError)
      return { errors: error.graphQLErrors, data: null }

    throw error
  }

  private static authorize(operation: Operation): void {
    // eslint-disable-next-line effector/no-getState
    const token = $$auth.token.$access.getState()
    if (!token) return // do not authorize logged-out users

    const prev = operation.getContext() as ClientContext

    const next = produce(prev, (draft) => {
      if (!prev.headers) draft.headers = {}
      draft.headers!.Authorization = `Bearer ${token}`
    })

    operation.setContext(next)
  }
}

export function isExpiredError({ extensions }: GraphQLFormattedError): boolean {
  return extensions?.code === GraphQLErrorCode.EXPIRED_SESSION
}

interface SubscriptionObserver<T> {
  next?(value: T): void
  error?(errorValue: unknown): void
  complete?(): void
}

/** A spy on Observable, allowing side-effects to happen without disrupting the data flow */
function tap<T>(
  obs: Observable<T>,
  watcher: SubscriptionObserver<T>,
): Observable<T> {
  return new Observable((observer) =>
    obs.subscribe({
      next: (data) => (watcher.next?.(data), observer.next(data)),
      error: (error) => (watcher.error?.(error), observer.error(error)),
      complete: () => (watcher.complete?.(), observer.complete()),
    }),
  )
}
